///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { LinkFeature } from '../../../entities/link/link-feature';
import { GtthHistoryService } from '../../../engine/history-service';
import { LinkInfoStateService } from '../link-info-state.service';
import { GtthProjectStateService } from '../../../engine/project-state.service';
import { SuiSelectControlComponent, SuiSelectEnumControlComponent, SuiTypeEnum, SuiTypeSelect } from '@siklu/sui-lib/infrastructure';
import { EngineAntennaDTO } from '../../../../../generated/shared-types.model';
import { LinkProductsPairModel } from './link-products-pair.model';
import { SuiTranslateEnumPipe } from '@siklu/sui-lib/localization';
import { LinkMarks } from '../../../entities/link/link-marks';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface FrequencyOption {
    title: string;
    value: number;
    autoOption: boolean;
}

@Component({
    selector: 'winde-link-info-panel',
    templateUrl: 'link-info-panel.component.html',
    styleUrls: ['../../entities-info-window.scss'],
    imports: [SuiSelectControlComponent, SuiTranslateEnumPipe, SuiSelectEnumControlComponent]
})
export class LinkInfoPanelComponent implements OnInit {
    public readonly link: LinkFeature;
    public readonly readonlyMode: boolean;
    public radiosSelect: SuiTypeSelect<LinkProductsPairModel>;
    public frequencySelect: SuiTypeSelect<FrequencyOption>;
    public linkState: SuiTypeEnum<LinkMarks>;
    public antennaFromSelect: SuiTypeSelect<EngineAntennaDTO>;
    public antennaToSelect: SuiTypeSelect<EngineAntennaDTO>;

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly linkInfoStateService: LinkInfoStateService,
        historyService: GtthHistoryService,
        projectStateService: GtthProjectStateService
    ) {
        this.link = this.linkInfoStateService.getLink();
        this.readonlyMode =
            !historyService.isEditModeEnabled ||
            projectStateService.isBomConfigProject() ||
            projectStateService.isPotentiallyCorruptedProject();
    }

    ngOnInit(): void {
        this.initLinkStateSelect();
        this.initRadioSelect();
        this.initAntennaSelects();
        this.initFrequencySelect();
    }

    private initLinkStateSelect(): void {
        this.linkState = new SuiTypeEnum(
            this.linkInfoStateService.getLinkState(),
            [LinkMarks.LINK, LinkMarks.FROZEN, LinkMarks.INSTALLED],
            'LinkMarks'
        );
        this.linkState.isAlignedStyles = true;
        this.linkState.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.linkState.valueChanges$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.linkInfoStateService.setLinkState(this.linkState.getValue()));
    }

    private initRadioSelect(): void {
        const availableProductPairs = this.linkInfoStateService.getAvailableProductPairs();
        const currentProducts = this.linkInfoStateService.getSelectedProductsPair();
        this.radiosSelect = new SuiTypeSelect(currentProducts, availableProductPairs, 'title');
        this.radiosSelect.isAlignedStyles = true;
        this.radiosSelect.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.radiosSelect.valueChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.linkInfoStateService.changeRadioPair(this.radiosSelect.getValue());

            this.linkState?.setValue(this.linkInfoStateService.getLinkState());

            this.antennaFromSelect?.setOptions(this.linkInfoStateService.getFromAvailableAntennas());
            this.antennaFromSelect?.setValue(this.linkInfoStateService.getFromAntenna());

            this.antennaToSelect?.setOptions(this.linkInfoStateService.getToAvailableAntennas());
            this.antennaToSelect?.setValue(this.linkInfoStateService.getToAntenna());

            this.frequencySelect?.setOptions(this.linkInfoStateService.getAvailableFrequencies());
            this.frequencySelect?.setValue(this.linkInfoStateService.getLinkFrequency());
        });
    }

    private initAntennaSelects(): void {
        this.antennaFromSelect = new SuiTypeSelect(
            this.linkInfoStateService.getFromAntenna(),
            this.linkInfoStateService.getFromAvailableAntennas(),
            'name'
        );
        this.antennaFromSelect.isAlignedStyles = true;
        this.antennaFromSelect.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.antennaFromSelect.valueChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.linkInfoStateService.changeFromAntenna(this.antennaFromSelect.getValue());
        });

        this.antennaToSelect = new SuiTypeSelect(
            this.linkInfoStateService.getToAntenna(),
            this.linkInfoStateService.getToAvailableAntennas(),
            'name'
        );
        this.antennaToSelect.isAlignedStyles = true;
        this.antennaToSelect.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.antennaToSelect.valueChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.linkInfoStateService.changeToAntenna(this.antennaToSelect.getValue());
        });
    }

    private initFrequencySelect(): void {
        this.frequencySelect = new SuiTypeSelect(
            this.linkInfoStateService.getLinkFrequency(),
            this.linkInfoStateService.getAvailableFrequencies(),
            'title'
        );
        this.frequencySelect.isAlignedStyles = true;
        this.frequencySelect.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.frequencySelect.valueChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.linkInfoStateService.changeFrequency(this.frequencySelect.getValue());

            this.linkState.setValue(this.linkInfoStateService.getLinkState());
        });
    }
}
